import { useQuery } from 'react-query'
import {
  forEach,
  pathOr,
  isEmpty,
  indexBy,
  prop,
  toPairs,
  values,
  compose,
  filter,
  map,
} from 'ramda'
import localStorageUtil from '@lib/utils/localstorage'
import useComapnyCode from '@/hooks/useComapnyCode'
import useAuth, { UserSpecialFunctionTypeEnum } from '.'
import type { FunctionAuth, MenuAuth, User, UserPermission } from './auth.types'
// import fake from './mineinfo.fake.json'
import { setUserAuth } from './useLogin'

export const useMineInfo = (
  options: {
    onSuccess?: (user: User) => void
    onError?: (error: any) => void
  } = {}
) => {
  const { onSuccess, onError } = options
  const { setIsAuthenticated, setUser } = useAuth()
  const companyCode = useComapnyCode()

  const { refetch, isLoading } = useQuery(['/account/mineInfo'], {
    cacheTime: Infinity,
    refetchOnMount: false,
    enabled: false,
    onError: error => {
      console.log(error)
      onError && onError(error)
      setIsAuthenticated(false)
    },
    onSuccess: (rs: any) => {
      if (!rs) {
        return
      }

      const response = rs

      const isGhgProtocol = localStorageUtil.get('menu_std') === 'ghg'

      const authDetails = pathOr(
        [],
        ['data', 'roleObj', 'authDetails'],
        response
      )

      const ghgMenus = pathOr([], ['data', 'roleObj', 'ghgMenus'], response)

      let authById: { [key: string]: UserPermission } = {}
      let authByUrl: { [key: string]: UserPermission } = {}
      let authByActType: { [key: string]: UserPermission } = {}

      if (isGhgProtocol) {
        forEach((x: MenuAuth) => {
          let ghg_C1C5_Path
          const originalPath = x.webPath.split('/').pop()

          const isGhgC1Path =
            x.webPath.indexOf('ghg_C1_') > -1 && !x.isSubMenuIcon
          const isGhgC5Path =
            x.webPath.indexOf('ghg_C5_') > -1 && !x.isSubMenuIcon
          const isGhgC1SubMenu =
            x.webPath.indexOf('ghg_C1_') > -1 && x.isSubMenuIcon
          const isGhgC5SubMenu =
            x.webPath.indexOf('ghg_C5_') > -1 && x.isSubMenuIcon

          if (x.actType) {
            authByActType[x.actType] = setUserAuth(x, authByActType[x.actType])
          }

          if (!isGhgC1Path && !isGhgC5Path) {
            if (!isGhgC1SubMenu && !isGhgC5SubMenu) {
              authByUrl[x.webPath] = setUserAuth(x, authByUrl[x.webPath])
            } else if (isGhgC1SubMenu || isGhgC5SubMenu) {
              const replacePath = isGhgC1SubMenu ? 'ghg_C1' : 'ghg_C5'

              ghg_C1C5_Path = originalPath
                ? x.webPath.replace(originalPath, replacePath)
                : x.webPath
              // 新增 ghgParentPath
              const obj = { ...x, ghgParentPath: ghg_C1C5_Path } as any
              authByUrl[x.webPath] = setUserAuth(obj, authByUrl[x.webPath])
            }
          } else {
            const replacePath = isGhgC1Path ? 'ghg_C1' : 'ghg_C5'
            ghg_C1C5_Path = originalPath
              ? x.webPath.replace(originalPath, replacePath)
              : x.webPath

            const obj = { ...x, webPath: ghg_C1C5_Path } as any

            if (ghg_C1C5_Path) {
              authByUrl[ghg_C1C5_Path] = setUserAuth(
                obj,
                authByUrl[ghg_C1C5_Path]
              )
            }
          }

          //-- [authById] green
          if (isEmpty(companyCode) && x.code.startsWith('admin_')) {
            authById[x.name] = setUserAuth(x, authById[x.name])
            return
          }
          //-- [authById] blue
          if (companyCode && !x.code.startsWith('admin_')) {
            if (!isGhgC1Path && !isGhgC5Path) {
              authById[x.name] = setUserAuth(x, authById[x.name])
            } else {
              const obj = { ...x, webPath: ghg_C1C5_Path, originalPath } as any
              authById[x.name] = setUserAuth(obj, authById[x.name])
            }
          }
        })(ghgMenus)
      } else {
        // authDetails 原有的邏輯
        forEach((x: MenuAuth) => {
          authByUrl[x.webPath] = setUserAuth(x, authByUrl[x.webPath])

          if (isEmpty(companyCode) && x.code.startsWith('admin_')) {
            authById[x.name] = setUserAuth(x, authById[x.name])
            return
          }

          if (companyCode && !x.code.startsWith('admin_')) {
            authById[x.name] = setUserAuth(x, authById[x.name])

            if (x.actType) {
              authByActType[x.actType] = setUserAuth(
                x,
                authByActType[x.actType]
              )
            }
          }
        })(authDetails)
      }
      //--funcList
      const funcList = Object.keys(authById).map(i => authById[i])

      const { functionAuthByUrl, specialFunctionAuth, functionAuthByCode } =
        prepareFunctionAuth(response, authByUrl, isGhgProtocol)

      const systemLangs: any = compose<any, any, any, any>(
        map((i: any) => i.code.replace('LANG_PREF_', '')),
        filter((i: any) => i.code.includes('LANG_PREF_')),
        pathOr([], ['data', 'roleObj', 'functionAuthDetails'])
      )(response)
      const companyObj: any = pathOr(null, ['data', 'companyObj'], response)
      const memberRoleId: any = pathOr(
        null,
        ['data', 'roleObj', 'id'],
        response
      )
      const sessionInfoId: any = pathOr(
        null,
        ['data', 'sessionInfos', 0, 'id'],
        response
      )

      const userInfo = {
        ...response.data,
        authDetails,
        ghgMenus,
        memberRoleId,
        sessionInfoId,
        authById,
        authByUrl,
        authByActType,
        funcList,
        companyCode: companyObj?.companyCode,
        companySeq: companyObj?.id,
        isCompanyAccount: !!companyObj,
        needResetPassword: pathOr(
          null,
          ['data', 'needResetPassword'],
          response
        ),
        functionAuthByUrl,
        specialFunctionAuth,
        functionAuthByCode,
        systemLangs,
      } as any

      localStorageUtil.set('user', userInfo)
      setUser(userInfo)
      setIsAuthenticated(true)
      onSuccess && onSuccess(userInfo)
    },
  })

  return { refetch, isLoading }
}

export const useKeepTokenAlive = () => {
  const { refetch } = useQuery(['/account/testAlive'], {
    cacheTime: 60 * 1000,
    enabled: false,
  })
  return {
    refetch,
  }
}

/**
 * functionAuthDetails(後端): 匯入的權限
 */
const prepareFunctionAuth = (
  response: any,
  authByUrl: { [key: string]: any },
  isGhgProtocol?: boolean
) => {
  const functionAuths: FunctionAuth[] = pathOr(
    [],
    ['data', 'roleObj', 'functionAuthDetails'],
    response
  )

  const authByUrlValues: any[] = Object.values(authByUrl)

  const functionAuthByCode = indexBy(prop('code'), functionAuths)

  const functionAuthByUrl: any = {}
  //-- menu_std is ISO
  if (!isGhgProtocol) {
    const codeAuthDetailByCode = indexBy(prop('code'), authByUrlValues)

    toPairs(codeAuthDetailByCode).forEach(
      ([code, auth]: [string, MenuAuth]) => {
        const importFunctionAuth: FunctionAuth | null =
          functionAuthByCode[`${code}_import`]
        // webPath的啟用
        const enableFunctionAuth: FunctionAuth | null =
          functionAuthByCode[`${code}_enable`]
        // webPath的停用
        const disableFunctionAuth: FunctionAuth | null =
          functionAuthByCode[`${code}_disable`]

        functionAuthByUrl[auth.webPath] = {
          import: importFunctionAuth ? importFunctionAuth.authExe : null,
          authEnable: enableFunctionAuth ? enableFunctionAuth.authExe : null,
          authDisable: disableFunctionAuth ? disableFunctionAuth.authExe : null,
        }
      }
    )
  } else {
    //-- menu_std = GHG
    const codeAuthDetailByCode = indexBy(prop('webPath'), authByUrlValues)

    toPairs(codeAuthDetailByCode).forEach(
      ([webPath, auth]: [string, MenuAuth]) => {
        const code = auth.code
        const importFunctionAuth: FunctionAuth | null =
          functionAuthByCode[`${code}_import`]
        // webPath的啟用
        const enableFunctionAuth: FunctionAuth | null =
          functionAuthByCode[`${code}_enable`]
        // webPath的停用
        const disableFunctionAuth: FunctionAuth | null =
          functionAuthByCode[`${code}_disable`]

        if (auth.ghgParentPath) {
          functionAuthByUrl[auth.ghgParentPath] = {
            import: importFunctionAuth ? importFunctionAuth.authExe : null,
            authEnable: enableFunctionAuth ? enableFunctionAuth.authExe : null,
            authDisable: disableFunctionAuth
              ? disableFunctionAuth.authExe
              : null,
          }
        }

        functionAuthByUrl[auth.webPath] = {
          import: importFunctionAuth ? importFunctionAuth.authExe : null,
          authEnable: enableFunctionAuth ? enableFunctionAuth.authExe : null,
          authDisable: disableFunctionAuth ? disableFunctionAuth.authExe : null,
        }
      }
    )
  }

  const specialFunctionAuth: { [key: string]: boolean } = {}
  values(UserSpecialFunctionTypeEnum).forEach(authKey => {
    specialFunctionAuth[authKey] = functionAuthByCode[authKey]?.authExe || false
  })

  return {
    functionAuthByUrl,
    specialFunctionAuth,
    functionAuthByCode,
  }
}
